import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Calendar, MapPin, Share2, Tag } from 'lucide-react';
import { trackClick } from '@/services/tracking';
import { trackSelectPromotion } from '@/lib/analytics';
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { useRenderNow } from '@/hooks/useRenderNow';
import { entityPageRequests } from '@/graphql/entityRoutes';
import { shareUrl } from '@/utils/share';
import {
  CONTENT_COLUMN_SIZES,
  THUMB_SRCSET_WIDTHS,
  pickPromotionCover,
} from '@/utils/safeImageUrl';
import { entityPath, shareLink } from '@/seo/urls';
import { promotionPeriodShort, promotionPhase } from '@/seo/lifecycle';
import { displayName } from '@/seo/text';
import { Badge, Img } from '@/components/ui';
import type { Media, Promotion, PromotionMinimal } from '@/types';

type PromotionCardVariant = 'grid' | 'compact' | 'full';

interface PromotionCardProps {
  promotion: Promotion | PromotionMinimal;
  variant?: PromotionCardVariant;
  /**
   * The cover is likely the page's LCP element (the first card of a list, above
   * the fold): eager, `fetchpriority=high`, never faded in (audit F6).
   */
  priority?: boolean;
  /**
   * The title's heading level: 3 under a section's H2 (the default), 2 for a list
   * whose cards follow the page's H1 directly (/promotions; audit F16).
   */
  headingLevel?: 2 | 3;
}

/** Promotion artwork: THUMBNAIL, then VIDEO_THUMBNAIL, never the video playlist itself. */
const pickPromotionMedia = pickPromotionCover;

const IMAGE_ASPECT: Record<PromotionCardVariant, string> = {
  grid: 'aspect-[4/3]',
  compact: 'aspect-[3/2]',
  full: 'aspect-[16/9]',
};

/**
 * Rendered cover width per variant, for the srcset ladder. `grid` cards sit in
 * 2 / 3 / 4(–5) column grids inside the max-w-7xl column (≤ 300 px wide at
 * full width); `compact` is the fixed w-64 rail card. Without this the cover
 * was 480 px × device pixel ratio — w_1440 for a ~165 px phone slot.
 */
const IMAGE_SIZES: Record<PromotionCardVariant, string> = {
  grid: '(min-width: 1280px) 300px, (min-width: 1024px) 25vw, (min-width: 768px) 34vw, 50vw',
  compact: '256px',
  full: CONTENT_COLUMN_SIZES,
};

export function PromotionCard({ promotion, variant = 'grid', priority = false, headingLevel = 3 }: PromotionCardProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const [copied, setCopied] = useState(false);
  // Dates are read in the page's country's time zone.
  const locale = useRouteLocale();
  // The server's render time while hydrating, so the badge matches its HTML.
  const renderNow = useRenderNow();

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const media = pickPromotionMedia(promotion.media);
  // From the dates only (master D-4), never the `isUpcoming` flag: a flagged
  // promotion that already started is running, and a future one published
  // without the flag is still upcoming.
  const upcoming = promotionPhase(promotion, renderNow, locale) === 'upcoming';
  const locationCount = (promotion.stores?.length ?? 0) + (promotion.shops?.length ?? 0);
  const hasDates = Boolean(promotion.startDate || promotion.endDate);
  // Names are often pasted from social posts (𝗕𝗼𝗹𝗱 letters, emoji, hashtags):
  // what the card shows and announces is the backend's clean headline (B3.5,
  // staff-typed or the cleaned name), else the cleaned name, like the
  // promotion page's H1. Analytics and the share sheet keep the stored name.
  const title = displayName(promotion.headline) || displayName(promotion.name);
  const brandName = displayName(promotion.brand?.name);

  const handleClick = () => {
    trackClick('promotion', promotion.id);
    trackSelectPromotion({
      promotionId: promotion.id,
      promotionName: promotion.name,
      creativeSlot: 'promotion_card',
    });
  };

  // The promotion's canonical path (its `urlPath` when the data has it, else
  // the id path; web plan W0.16), for the link and the share. Warm the Apollo
  // cache with the queries that page runs once the pointer rests on the card
  // (or it takes keyboard focus) so the first tap into it paints instantly.
  // At most once per card instance; see useHoverPrefetch.
  const to = entityPath(promotion, 'promotion');
  const prefetchDetail = useHoverPrefetch(entityPageRequests('promotion', promotion));

  const handleShare = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    shareUrl(promotion.name, shareLink(to), {
      contentType: 'promotion',
      itemId: promotion.id,
    })
      .then((result) => {
        if (result === 'copied') setCopied(true);
      })
      .catch(() => {});
  };

  // The share button is a sibling of the link, not inside it: interactive
  // content nested in an <a> is invalid HTML and reads as one muddled link to
  // screen readers. The wrapper carries the hover lift so both move together.
  return (
    <div
      className={`group relative transition-transform duration-200 hover:-translate-y-0.5 ${
        variant === 'compact' ? 'w-64 shrink-0' : ''
      }`}
    >
      <Link
        to={to}
        onClick={handleClick}
        {...prefetchDetail}
        className="block h-full overflow-hidden rounded-2xl border border-card-border bg-card transition-shadow duration-200 group-hover:shadow-lg"
      >
        {/* Image area */}
        <div className={`relative overflow-hidden bg-surface ${IMAGE_ASPECT[variant]}`}>
          <Img
            media={media}
            transform={{ width: variant === 'full' ? 800 : 480, fit: 'cover' }}
            sizes={IMAGE_SIZES[variant]}
            alt={title}
            priority={priority}
            className="h-full w-full object-cover"
            fallback={
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/60 to-secondary/60">
                <Tag size={32} className="text-white/80" />
              </div>
            }
          />

          {/* Top-left badges */}
          {(upcoming || promotion.verified === 'VERIFIED') && (
            <div className="absolute left-2 top-2 z-10 flex items-center gap-1.5">
              {upcoming && <Badge color="warning">Upcoming</Badge>}
              {promotion.verified === 'VERIFIED' && (
                <span
                  className="flex rounded-full bg-surface/90 p-0.5 shadow backdrop-blur"
                  title="Verified promotion"
                >
                  <BadgeCheck size={16} className="text-success" aria-hidden="true" />
                  {/* `title` is mouse-only; this is what the link announces. */}
                  <span className="sr-only">Verified promotion</span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-3">
          {promotion.brand && (
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className="h-[18px] w-[18px] shrink-0 overflow-hidden rounded-full border border-line bg-surface">
                <Img
                  media={promotion.brand.logo}
                  transform={{ width: 64, fit: 'cover' }}
                  sizes="16px"
                  widths={THUMB_SRCSET_WIDTHS}
                  // Decorative: the brand's name is the text right beside it.
                  alt=""
                  className="h-full w-full object-cover"
                  fallback={<span className="block h-full w-full bg-primary/15" />}
                />
              </span>
              <span className="text-xs text-ink-secondary line-clamp-1">{brandName}</span>
            </div>
          )}

          <Heading
            className={`font-extrabold text-ink line-clamp-2 break-words ${
              variant === 'compact' ? 'text-sm' : 'text-sm md:text-base'
            }`}
          >
            {title}
          </Heading>

          <div className="mt-2 flex items-center justify-between gap-2">
            {locationCount > 0 ? (
              <span className="flex shrink-0 items-center gap-1 rounded-md bg-primary/15 px-2 py-0.5 text-xs font-bold text-primary-strong">
                <MapPin size={12} />
                {locationCount} {locationCount === 1 ? 'location' : 'locations'}
              </span>
            ) : (
              <span />
            )}
            {hasDates && (
              <span className="flex min-w-0 items-center gap-1 text-xs text-ink-tertiary">
                <Calendar size={12} className="shrink-0" />
                {/* Locale time zone, like the promotion page's own period. */}
                <span className="truncate">{promotionPeriodShort(promotion, locale)}</span>
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Top-right share button, over the image area */}
      <div className="absolute right-2 top-2 z-10">
        <button
          type="button"
          onClick={handleShare}
          aria-label={`Share ${title}`}
          className="rounded-full bg-surface/90 p-2 text-ink shadow backdrop-blur transition-all duration-200 hover:bg-surface"
        >
          <Share2 size={14} aria-hidden="true" />
        </button>
        {/* Always mounted: a live region only announces changes to itself. */}
        <span role="status">
          {copied && (
            <span className="absolute right-0 top-full mt-1 whitespace-nowrap rounded-md bg-ink px-2 py-0.5 text-xs font-semibold text-surface shadow">
              Copied!
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

export default PromotionCard;
